import { Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import { DfpTable } from 'components/molecules/Dokumenty/DfpTable';
import { DfpTableDlt } from 'components/molecules/Dokumenty/DfpTableDlt';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';

import {
    IZruseniPpnPrikaz,
    IZruseniPpnPrikazPut,
    PrikazZrusPpnGet,
    PrikazZrusPpnPut
} from 'services/dokumenty/prikazy';
import { ISmlouvniStrany } from 'services/dokumenty/smlouva';

import { isIsinDlt } from '../NovyDokument';
import { getDataIsins } from './Zruseni';

interface IZruseniPPN {
    nextStep: () => void;
    zadostID: number;
    prikazyData: ISmlouvniStrany;
}

const ZruseniPPN: React.FC<IZruseniPPN> = ({ nextStep, zadostID, prikazyData }) => {
    const [form] = Form.useForm();
    const { dokumenty } = useContext(Context);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [data, setData] = useState<IZruseniPpnPrikaz>();

    const saveData = (vals: any) => {
        const dataToSend = {
            ...(vals.datum && {
                datum: vals.datum.format('YYYY-MM-DD')
            }),
            isin: vals.isin,
            zrusitppn: vals.zrusitppn
        } as IZruseniPpnPrikazPut;

        return PrikazZrusPpnPut(zadostID, dataToSend);
    };

    const [DfpTableDltRender, DfpUcetDltRender, displayModeDfpDlt, dataUctyDlt] = DfpTableDlt(
        zadostID,
        () => {
            saveData(form.getFieldsValue());
            setData(pv => {
                return {
                    ...pv,
                    ...form.getFieldsValue()
                };
            });
        },
        form,
        true,
        ['isin', 'datum']
    );
    const [DfpTableRender, DfpUcetRender, SouborRender, displayMode, dataUcty] = DfpTable(
        zadostID,
        () => {
            saveData(form.getFieldsValue());
            setData(pv => {
                return {
                    ...pv,
                    ...form.getFieldsValue()
                };
            });
        },
        form,
        true,
        undefined,
        undefined,
        undefined,
        ['isin', 'datum']
    );

    const [dataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
        getDataIsins(dokumenty).map(e => {
            return {
                labelcz: e.isin,
                value: e.isin
            };
        }) || []
    );

    const [selectedIsin, setSelectIsin] = useState<string>();
    const [isDLT, setIsDLT] = useState<boolean>();
    useEffect(() => {
        if (selectedIsin) {
            setIsDLT(isIsinDlt(selectedIsin, dokumenty!));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIsin]);

    useEffect(() => {
        setLoading(true);
        PrikazZrusPpnGet(zadostID)
            .then(resp => {
                setData(resp);
                setSelectIsin(resp.isin);
                form.setFieldsValue({
                    zrusitppn: resp.zrusitppn
                });
            })
            .catch(() => setData({} as IZruseniPpnPrikaz))
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    return (
        <LoadingWrapper loading={loading}>
            {data &&
                prikazyData &&
                (displayMode === 'soubor' ? (
                    SouborRender()
                ) : displayMode === 'ucet' ? (
                    DfpUcetRender()
                ) : displayModeDfpDlt === 'ucet' ? (
                    DfpUcetDltRender()
                ) : (
                    <FormWrapper
                        outForm={form}
                        onFinish={values => {
                            setLoading(true);
                            saveData(values)
                                .then(() => nextStep())
                                .finally(() => setLoading(false));
                        }}
                        preserve={true}
                    >
                        <Title text="EMITENT" level={5} />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmbSpolecnost"
                            notRequired={true}
                        />
                        {prikazyData?.spolecnost.dicdph && (
                            <InputComponent
                                disabled={true}
                                initialValue={prikazyData?.spolecnost.dicdph}
                                label="ISIN_IDENTIFIKACE_DIC"
                                name="dicdphSpolecnost"
                                notRequired={true}
                            />
                        )}
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.nazev}
                            label="ISIN_IDENTIFIKACE_FIRMA"
                            name="nazevSpolecnost"
                            notRequired={true}
                        />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.lei}
                            label="LEI_CELE"
                            name="leiSpolecnost"
                            notRequired={true}
                        />
                        <SelectComponent
                            name="isin"
                            labelParagraph={'PRIKAZ_ZNIZENI_ISIN'}
                            defaultValue={data?.isin}
                            isFormItem={true}
                            data={dataIsins}
                            disabled={!!(dataUctyDlt?.ucty || []).length || !!(dataUcty?.ucty || []).length}
                            onChange={val => {
                                setSelectIsin(val);
                            }}
                        />
                        <DatePickerComponent
                            name="datum"
                            initialValue={data?.datum ? dayjs(data.datum) : undefined}
                            //  disabledDate={current => current < dayjs()}
                            label="PRIKAZ_PREVOD_DATUM"
                        />
                        <SelectComponent
                            name="zrusitppn"
                            labelParagraph={'PRIKAZ_PPN_ZRUSIT'}
                            defaultValue={data?.zrusitppn}
                            isFormItem={true}
                            data={[
                                {
                                    labelcz: 'PRIKAZ_PPN_ZRUSIT_NAVSECHUCT',
                                    value: 'NAVSECHUCT'
                                },
                                {
                                    labelcz: 'PRIKAZ_PPN_ZRUSIT_NAUCTMAJIT',
                                    value: 'NAUCTMAJIT'
                                }
                            ]}
                            onChange={() => rerender(pv => !pv)}
                        />
                        {form.getFieldValue('zrusitppn') === 'NAUCTMAJIT' && (
                            <>
                                <Row>
                                    <Paragraph
                                        text="*"
                                        style={{
                                            color: '#ff4d4f',
                                            fontSize: 23,
                                            position: 'relative',
                                            top: 4,
                                            marginRight: 2
                                        }}
                                    />
                                    <Title level={5} text="VYPLNTE_UDAJE" />
                                </Row>
                                {isDLT ? DfpTableDltRender() : DfpTableRender()}
                            </>
                        )}
                        <PrilohyTable zadostID={zadostID} required={true} />
                        <Title level={5} text="PRIKAZ_VYTESNENI_PODMINKY_TITLE" />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_PPN_ZRUSIT_TEXT1_DLT' : 'PRIKAZ_PPN_ZRUSIT_TEXT1'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_PPN_ZRUSIT_TEXT2_DLT' : 'PRIKAZ_PPN_ZRUSIT_TEXT2'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Row
                            style={{
                                marginTop: 26
                            }}
                        >
                            <CheckboxComponent
                                name="souhlas"
                                checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                                onChange={() => setIsChecked(pv => !pv)}
                                checked={isChecked}
                            />
                        </Row>
                        <Row className="button-row">
                            <Col>
                                <ButtonComponent
                                    buttontext="POKRACOVAT"
                                    id="isinPokracovatBtn"
                                    disabled={!isChecked}
                                    htmlType="submit"
                                />
                            </Col>
                        </Row>
                    </FormWrapper>
                ))}
        </LoadingWrapper>
    );
};

export default ZruseniPPN;
